import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, Users } from "lucide-react";
import { normalizeImageUrl } from "@/lib/imageUrl";

const BT = (b, t) => (b || t) ? `${b || "—"}/${t || "—"}` : null;
const posLabel = (p) => (p || "").toUpperCase();

function Avatar({ p }) {
  return (
    <div className="w-10 h-10 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0 border border-slate-200">
      {p.profile_photo_url
        ? <img src={normalizeImageUrl(p.profile_photo_url)} alt={`${p.first_name} ${p.last_name}`} className="w-full h-full object-cover" />
        : <span className="text-[11px] font-semibold text-slate-400">{(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}</span>}
    </div>
  );
}

const SORTS = [
  ["number", "Jersey #"], ["name", "Name"], ["position", "Position"], ["graduation_year", "Grad Year"],
];

export default function TeamRosterTab({ roster = [] }) {
  const [sort, setSort] = useState("number");
  const [dir, setDir] = useState("asc");
  const [posFilter, setPosFilter] = useState("");

  const positions = useMemo(() => {
    const s = new Set();
    roster.forEach((p) => { if (p.primary_position) s.add(p.primary_position); });
    return [...s].sort();
  }, [roster]);

  const rows = useMemo(() => {
    let r = posFilter ? roster.filter((p) => p.primary_position === posFilter || p.secondary_position === posFilter) : [...roster];
    const cmp = (a, b) => {
      let av, bv;
      if (sort === "number") { av = a.number ?? 9999; bv = b.number ?? 9999; }
      else if (sort === "name") { av = `${a.last_name || ""} ${a.first_name || ""}`.toLowerCase(); bv = `${b.last_name || ""} ${b.first_name || ""}`.toLowerCase(); }
      else if (sort === "position") { av = a.primary_position || "zzz"; bv = b.primary_position || "zzz"; }
      else { av = a.graduation_year ?? 9999; bv = b.graduation_year ?? 9999; }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    };
    return r.sort(cmp);
  }, [roster, sort, dir, posFilter]);

  if (!roster.length) return <div data-testid="team-roster-tab"><div className="py-12 text-center text-sm text-slate-400" data-testid="roster-empty">No players on the roster yet.</div></div>;

  const toggleSort = (key) => { if (sort === key) setDir((d) => d === "asc" ? "desc" : "asc"); else { setSort(key); setDir("asc"); } };

  return (
    <div data-testid="team-roster-tab">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5 text-sm text-slate-500 mr-1"><Users className="w-4 h-4 text-[#E63946]" /> {rows.length} player{rows.length === 1 ? "" : "s"}</div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="roster-sort">Sort roster</label>
          <select id="roster-sort" data-testid="roster-sort" value={sort} onChange={(e) => toggleSort(e.target.value)}
            className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none">
            {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
          </select>
          <button data-testid="roster-dir" onClick={() => setDir((d) => d === "asc" ? "desc" : "asc")} aria-label={`Sort ${dir === "asc" ? "ascending" : "descending"}`}
            className="inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white text-slate-600 hover:border-slate-400 focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none">
            <ArrowUpDown className="w-3.5 h-3.5" /> {dir === "asc" ? "Asc" : "Desc"}
          </button>
          {positions.length > 0 && (
            <>
              <label className="sr-only" htmlFor="roster-pos">Filter by position</label>
              <select id="roster-pos" data-testid="roster-filter-position" value={posFilter} onChange={(e) => setPosFilter(e.target.value)}
                className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none">
                <option value="">All positions</option>
                {positions.map((p) => <option key={p} value={p}>{posLabel(p)}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-sm bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th scope="col" className="px-3 py-2.5 w-12">#</th>
              <th scope="col" className="px-3 py-2.5">Player</th>
              <th scope="col" className="px-3 py-2.5">Pos</th>
              <th scope="col" className="px-3 py-2.5">Grad</th>
              <th scope="col" className="px-3 py-2.5">Ht</th>
              <th scope="col" className="px-3 py-2.5">Wt</th>
              <th scope="col" className="px-3 py-2.5">B/T</th>
              <th scope="col" className="px-3 py-2.5 text-right">CBR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.player_id} data-testid={`roster-row-${p.player_id}`} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-3 py-2.5 font-data text-slate-500">{p.number ?? "—"}</td>
                <td className="px-3 py-2.5">
                  <Link to={`/prospects/${p.slug || p.player_id}`} className="flex items-center gap-3 group focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none rounded-sm">
                    <Avatar p={p} />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0A192F] group-hover:text-[#E63946] truncate">{p.first_name} {p.last_name}</div>
                      {p.commitment && <div className="text-[11px] text-emerald-700 truncate">◆ {p.commitment}</div>}
                    </div>
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{posLabel(p.primary_position) || "—"}{p.secondary_position ? <span className="text-slate-400">/{posLabel(p.secondary_position)}</span> : ""}</td>
                <td className="px-3 py-2.5 font-data text-slate-600">{p.graduation_year || "—"}</td>
                <td className="px-3 py-2.5 font-data text-slate-600">{p.height_cm ? `${p.height_cm} cm` : "—"}</td>
                <td className="px-3 py-2.5 font-data text-slate-600">{p.weight_kg ? `${p.weight_kg} kg` : "—"}</td>
                <td className="px-3 py-2.5 font-data text-slate-600">{BT(p.bats, p.throws) || "—"}</td>
                <td className="px-3 py-2.5 font-data text-right text-[#0A192F]">{p.cbr_overall_rating ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map((p) => (
          <Link key={p.player_id} to={`/prospects/${p.slug || p.player_id}`} data-testid={`roster-card-${p.player_id}`}
            className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm p-3 hover:border-[#E63946] transition-colors focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none">
            <div className="w-8 text-center font-data text-slate-400 shrink-0">{p.number ?? "—"}</div>
            <Avatar p={p} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[#0A192F] truncate">{p.first_name} {p.last_name}</div>
              <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                <span>{posLabel(p.primary_position) || "—"}{p.secondary_position ? `/${posLabel(p.secondary_position)}` : ""}</span>
                {p.graduation_year && <span>· {p.graduation_year}</span>}
                {BT(p.bats, p.throws) && <span>· {BT(p.bats, p.throws)}</span>}
                {(p.height_cm || p.weight_kg) && <span>· {[p.height_cm ? `${p.height_cm}cm` : null, p.weight_kg ? `${p.weight_kg}kg` : null].filter(Boolean).join(" ")}</span>}
              </div>
              {p.commitment && <div className="text-[11px] text-emerald-700 truncate">◆ {p.commitment}</div>}
            </div>
            {p.cbr_overall_rating != null && <span className="font-data text-sm text-[#0A192F] shrink-0">{p.cbr_overall_rating}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}
